import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth, isManager } from "@/context/AuthContext";
import AttendanceOverview from "@/pages/AttendanceOverview";
import { PageHeader } from "@/components/shared/PagePrimitives";
import WorkerAbsence from "@/components/absences/WorkerAbsence";
import { formatTime, formatDate, formatHours } from "@/lib/format";
import { PhotoThumb } from "@/components/shared/PhotoLightbox";
import { AlertTriangle, Coffee, CheckCircle2, Pencil } from "lucide-react";

// Dělník, skladník a subdodavatel (246): Docházka = jen kalendář — směny, hodiny, schválení
// i nepřítomnost po dnech (WorkerAbsence). Dřívější záložka „Můj výkaz“ ukazovala totéž znovu.
const CALENDAR_ROLES = ["WORKER", "WAREHOUSE", "SUBCONTRACTOR", "ACCOUNTANT"];

function WorkerAttendance() {
  const { user } = useAuth();
  if (CALENDAR_ROLES.includes(user?.role)) {
    return (
      <div className="space-y-6" data-testid="attendance-page">
        <PageHeader title="DOCHÁZKA" subtitle="Kde jsem byl, kolik hodin a volno" />
        <WorkerAbsence />
      </div>
    );
  }
  return <MonthList />;
}

// Měsíční seznam vlastních směn — pro ostatní role bez kalendáře.
function MonthList() {
  const [rows, setRows] = useState([]);
  const month = new Date().toISOString().slice(0, 7);
  useEffect(() => { api.get(`/attendance/monthly?month=${month}`).then((r) => setRows(r.data.rows || [])).catch(() => {}); }, [month]);
  const total = rows.reduce((s, r) => s + (r.hours || 0), 0);
  return (
    <div className="space-y-6" data-testid="attendance-page">
      <PageHeader title="DOCHÁZKA" subtitle="Moje docházka tento měsíc" />
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold">Tento měsíc</h2>
        {rows.length > 0 && <span className="text-sm font-bold text-brand" data-testid="month-total">{formatHours(total)}</span>}
      </div>
      {rows.length > 0 ? (
        <div className="bg-card border border-border rounded-[20px] divide-y divide-border">
          {rows.map((r) => (
            <div key={r.id} className="p-4" data-testid={`my-att-${r.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-semibold">{formatDate(r.date)} · {r.project}</span>
                <span className="text-sm font-semibold whitespace-nowrap">{formatTime(r.check_in)}–{r.check_out ? formatTime(r.check_out) : "…"} · {formatHours(r.hours)}</span>
              </div>
              {r.break_minutes != null && r.break_minutes > 0 && <div className="text-xs text-muted-foreground inline-flex items-center gap-1 mt-0.5" data-testid={`my-break-${r.id}`}><Coffee size={12} /> přestávka {r.break_minutes} min</div>}
              {(r.self_declared || r.selfDeclared) && (
                <span className="inline-flex items-center h-5 px-2 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 text-[11px] font-bold whitespace-nowrap" title="Hodiny doplnil pracovník zpětně po automatickém ukončení — nevznikly reálným odhlášením.">
                  Doplněno zpětně
                </span>
              )}
              {(r.overtime_beyond_auto || r.overtimeBeyondAuto) && (
                <span className="inline-flex items-center h-5 px-2 rounded-full bg-destructive text-white text-[11px] font-bold whitespace-nowrap" title="Pracovník uvedl konec po automatickém odhlášení. Hodiny nad rámec naměřeného času — potvrzujte obezřetně.">
                  Přesčas nad rámec
                </span>
              )}
              {r.auto_closed && (
                <div className="text-xs font-semibold text-destructive flex items-center gap-1 mt-1" data-testid={`my-auto-closed-${r.id}`}>
                  <AlertTriangle size={12} /> Nebyl jste řádně odhlášen — směna byla ukončena automaticky. Hodiny potvrdí stavbyvedoucí.
                </div>
              )}
              {r.report ? <p className="text-sm text-muted-foreground mt-1">{r.report}</p> : <p className="text-sm italic text-muted-foreground opacity-60 mt-1">bez reportu</p>}
              {(r.corrections || []).filter((c) => (c.note || "").trim()).map((c, i) => (
                <div key={i} className="text-xs text-muted-foreground flex items-center gap-1 mt-1" data-testid={`my-correction-${r.id}-${i}`}>
                  <Pencil size={11} /> Upraveno ({c.user_name}): {c.note}
                </div>
              ))}
              <div className="flex items-center gap-2 flex-wrap mt-1">
                {r.approval_status === "approved" && <span className="text-xs font-bold uppercase tracking-wide text-success rounded-md bg-success/10 px-1.5 py-0.5 inline-flex items-center gap-1" data-testid={`my-approved-${r.id}`}><CheckCircle2 size={12} /> schváleno</span>}
                {r.approval_status === "pending" && r.check_out && <span className="text-xs font-bold uppercase tracking-wide text-amber-500 rounded-md bg-amber-500/10 px-1.5 py-0.5" data-testid={`my-pending-${r.id}`}>čeká na schválení</span>}
                {!r.has_gps && <span className="text-xs text-amber-500 inline-flex items-center gap-1"><AlertTriangle size={12} /> bez GPS</span>}
                {r.out_of_radius && <span className="text-xs font-bold uppercase tracking-wide text-destructive rounded-md bg-destructive/10 px-1.5 py-0.5 inline-flex items-center gap-1" data-testid={`my-out-of-radius-${r.id}`}><AlertTriangle size={12} /> mimo stavbu{[r.checkin_distance_m, r.checkout_distance_m].filter((x) => x != null).length ? ` · ${Math.max(...[r.checkin_distance_m, r.checkout_distance_m].filter((x) => x != null))} m` : ""}</span>}
              </div>
              {(r.photos || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`my-att-photos-${r.id}`}>
                  {r.photos.map((url, i) => (
                    <PhotoThumb key={i} url={url} />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground bg-card border border-border rounded-[20px] p-4">Zatím žádné vlastní záznamy docházky tento měsíc.</p>
      )}
    </div>
  );
}

export default function Attendance() {
  const { user } = useAuth();
  return isManager(user) ? <AttendanceOverview /> : <WorkerAttendance />;
}
